import { useEffect, useRef, useState } from 'react';
import type { InsightCardPayload } from '../lib/insightCard';

const DWELL_MS = 3500;
const CHIP_IDLE_MS = 8000;

type Props = {
  /** Latest material card from the room (may change cardId / revision rapidly). */
  incoming: InsightCardPayload | null;
  visible: boolean;
};

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

function useCountUp(target: number, revision: number, durationMs = 700): number {
  const [display, setDisplay] = useState(target);
  const fromRef = useRef(0);
  const startRef = useRef(0);
  const rafRef = useRef(0);

  useEffect(() => {
    fromRef.current = display;
    startRef.current = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - startRef.current) / durationMs);
      const v = fromRef.current + (target - fromRef.current) * easeOutCubic(t);
      setDisplay(Math.round(v));
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
    // Restart tween only when revision or target changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- display is the tween source, not a dep
  }, [target, revision, durationMs]);

  return display;
}

function MetricCell({
  label,
  value,
  revision,
}: {
  label: string;
  value: number;
  revision: number;
}) {
  const n = useCountUp(value, revision);
  return (
    <div className="insight-metric">
      <span className="insight-metric-value">{n.toLocaleString()}</span>
      <span className="insight-metric-label">{label}</span>
    </div>
  );
}

function BarRow({
  label,
  value,
  max,
  revision,
}: {
  label: string;
  value: number;
  max: number;
  revision: number;
}) {
  const n = useCountUp(value, revision);
  const pct = Math.max(0, Math.min(100, (n / Math.max(max, 1)) * 100));
  return (
    <div className="insight-bar-row">
      <div className="insight-bar-meta">
        <span className="insight-bar-label">{label}</span>
        <span className="insight-bar-value">{n.toLocaleString()}</span>
      </div>
      <div className="insight-bar-track" aria-hidden>
        <div className="insight-bar-fill" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

/**
 * Metric card under the user PiP. Same React key per cardId (parent); dwell gates
 * cardId swaps; fades to a slim chip after idle.
 */
export function LiveInsightCard({ incoming, visible }: Props) {
  const [shown, setShown] = useState<InsightCardPayload | null>(null);
  const [pending, setPending] = useState<InsightCardPayload | null>(null);
  const [slim, setSlim] = useState(false);
  const dwellUntilRef = useRef(0);
  const lastShownAtRef = useRef(0);
  const idleTimerRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (!incoming) return;
    setShown((cur) => {
      if (!cur) {
        dwellUntilRef.current = Date.now() + DWELL_MS;
        lastShownAtRef.current = Date.now();
        setSlim(false);
        return incoming;
      }
      if (incoming.revision <= cur.revision && incoming.cardId === cur.cardId) {
        return cur;
      }
      if (incoming.cardId === cur.cardId) {
        dwellUntilRef.current = Date.now() + DWELL_MS;
        lastShownAtRef.current = Date.now();
        setSlim(false);
        return incoming;
      }
      // Different cardId — dwell before swap; keep at most one pending.
      if (Date.now() >= dwellUntilRef.current) {
        dwellUntilRef.current = Date.now() + DWELL_MS;
        lastShownAtRef.current = Date.now();
        setSlim(false);
        setPending(null);
        return incoming;
      }
      setPending(incoming);
      return cur;
    });
  }, [incoming]);

  useEffect(() => {
    if (!pending) return;
    const wait = Math.max(0, dwellUntilRef.current - Date.now());
    const t = window.setTimeout(() => {
      setShown(pending);
      setPending(null);
      dwellUntilRef.current = Date.now() + DWELL_MS;
      lastShownAtRef.current = Date.now();
      setSlim(false);
    }, wait);
    return () => window.clearTimeout(t);
  }, [pending]);

  useEffect(() => {
    if (!shown || !visible) return;
    if (idleTimerRef.current) window.clearTimeout(idleTimerRef.current);
    const ttl = typeof shown.ttlMs === 'number' ? shown.ttlMs : 45000;
    idleTimerRef.current = window.setTimeout(() => setSlim(true), Math.min(CHIP_IDLE_MS, ttl));
    const expire = window.setTimeout(() => {
      setShown(null);
      setPending(null);
      setSlim(false);
    }, ttl);
    return () => {
      if (idleTimerRef.current) window.clearTimeout(idleTimerRef.current);
      window.clearTimeout(expire);
    };
  }, [shown?.revision, shown?.cardId, shown?.ttlMs, visible]);

  if (!visible || !shown) return null;

  return (
    <aside
      className={`live-insight-card${slim ? ' is-slim' : ''}`}
      data-card-id={shown.cardId}
      data-revision={shown.revision}
      aria-live="polite"
      aria-label={`${shown.headline} insight`}
    >
      <div className="live-insight-card-head">
        <span className="live-insight-card-title">{shown.headline}</span>
        {shown.asOf ? <span className="live-insight-card-asof">{shown.asOf}</span> : null}
      </div>
      {!slim ? (
        <>
          {shown.metrics.length ? (
            <div className="insight-metrics">
              {shown.metrics.map((m) => (
                <MetricCell
                  key={m.key}
                  label={m.label}
                  value={m.value}
                  revision={shown.revision}
                />
              ))}
            </div>
          ) : null}
          {shown.bars.length ? (
            <div className="insight-bars">
              {shown.bars.map((b) => (
                <BarRow
                  key={b.id}
                  label={b.label}
                  value={b.value}
                  max={b.max || b.value || 1}
                  revision={shown.revision}
                />
              ))}
            </div>
          ) : null}
        </>
      ) : (
        <div className="insight-chip-summary">
          {shown.metrics
            .slice(0, 3)
            .map((m) => `${m.label} ${m.value.toLocaleString()}`)
            .join(' · ')}
        </div>
      )}
    </aside>
  );
}
